import React, { useCallback, useEffect, useRef, useState } from 'react';
import { RemoveScroll } from 'react-remove-scroll';

import { BasePopupActionPayload, BasePopupProps } from 'contexts/PopupContext';
import { FloatingBubbleAds } from 'types/interfaces/ads/FloatingBubbleAds';

import { useDynamicBannerAnalytics } from 'hooks/analytics/useDynamicBannerAnalytics';

import { ReactComponent as CloseIcon } from 'assets/icons/close.svg';

import { BaseButton, ButtonTypes } from 'components/base/BaseButton';
import { AdsCreative } from 'components/shared/AdsCreative';
import { Portal } from 'components/shared/Portal';

import css from './adsFloatingBubblePopup.module.sass';

type ActionsPayload = BasePopupActionPayload<'close'>;

type Props = BasePopupProps<
  FloatingBubbleAds,
  (action: ActionsPayload) => void
>;

export const AdsFloatingBubblePopup: React.FC<Props> = ({ data, onAction }) => {
  const animationCount = useRef(0);

  const [visibleText, setVisibleText] = useState<string | null>(null);

  const { trackBannerLoad } = useDynamicBannerAnalytics();

  const handleCloseClick = (e: any) => {
    e.stopPropagation();

    onAction({ type: 'close' });
  };

  const handleClick = useCallback(() => {
    onAction({ type: 'close' });
  }, [onAction]);

  const handleLoad = () => {
    trackBannerLoad({
      banner: data,
      requestTs: Date.now(),
    });
  };

  useEffect(() => {
    const makeAnimation = () => {
      if (!data?.text || !data?.text2) return;

      if (animationCount.current === 3) {
        return;
      }

      setTimeout(() => {
        if (animationCount.current === 0) {
          setVisibleText(data.text);
        } else if (animationCount.current === 1) {
          setVisibleText(null);
        } else {
          setVisibleText(data.text2);
        }

        makeAnimation();
        animationCount.current += 1;
      }, 1500);
    };

    makeAnimation();
  }, [data?.text, data?.text2]);

  useEffect(() => {
    if (!data) {
      handleClick();
    }
  }, [data, handleClick]);

  if (!data) {
    return null;
  }

  return (
    <Portal>
      <RemoveScroll>
        <div className={css.overlay}>
          <div className={css.root}>
            <BaseButton
              className={css.closeBtn}
              type={ButtonTypes.Transparent}
              onClick={handleCloseClick}
            >
              <CloseIcon className={css.closeIcon} />
            </BaseButton>
            <AdsCreative
              href={data.url}
              src={data.image_url}
              linkClassName={css.link}
              creativeClassName={css.image}
              onClick={handleClick}
              onLoad={handleLoad}
            >
              <section className={css.message}>
                {visibleText || (
                  <ul className={css.dotsList}>
                    <li className={css.dostItem} />
                    <li className={css.dostItem} />
                    <li className={css.dostItem} />
                  </ul>
                )}
              </section>
            </AdsCreative>
          </div>
        </div>
      </RemoveScroll>
    </Portal>
  );
};
